import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
} from "@angular/core";
import { Router } from "@angular/router";
import { ArticlesInfosFacadeService } from "app/features/articles/services/article-infos/article-infos-facade.service";
import { MatTooltipModule } from "@angular/material/tooltip";
import { SharedModule } from "../../shared.module";
import { SpecificationFacadeService } from "../../../features/specifications/services/specification-facade.service";
import { EtatSpecification } from "../../../features/specifications/enums/etat-specif.enum";
import { ChipsComponent } from "../../modules/chips/chips.component";
import { ChipState } from "../../enums/chip-state.enum";
import { NgClass } from "@angular/common";
import { DocLocalComponent } from "../../../features/documents/containers/doc-local/doc-local.component";
import { JustLogoComponent } from "../just-logo/just-logo.component";
import { BigButtonComponent } from "../big-button/big-button.component";
import { BioLogoComponent } from "../bio-logo/bio-logo.component";
import { PopoverModule } from "../../modules/popover/popover.module";
import { ArticleInfos } from "app/features/articles/models/article.model";

@Component({
  selector: "vl-article-small-card",
  templateUrl: "./article-small-card.component.html",
  styleUrls: ["./article-small-card.component.scss"],
  standalone: true,
  imports: [
    MatTooltipModule,
    SharedModule,
    ChipsComponent,
    NgClass,
    DocLocalComponent,
    JustLogoComponent,
    BigButtonComponent,
    BioLogoComponent,
    PopoverModule,
  ],
})
export class ArticleSmallCardComponent {
  articleInfosFacade = inject(ArticlesInfosFacadeService);
  specificationFacade = inject(SpecificationFacadeService);

  navigation = input(true);
  articleId = input<string>();
  articleInfosInput = input<ArticleInfos>();
  showVersions = input<boolean>(true);
  showIcons = input<boolean>(false);
  showVersionsWarn = input(true);
  sem = computed(() => {
    const articleId = this.articleId();
    if (articleId) {
      return this.specificationFacade.getByArticleId(articleId)?.();
    }
  });
  warning = output<boolean>();

  currentVersion = computed(() => {
    const sem = this.sem();
    return sem?.versions.find((v) => v.etat === EtatSpecification.EN_COURS);
  });

  lastVersion = computed(() => {
    const sem = this.sem();
    return sem?.versions
      .filter(
        (v) =>
          v.etat !== EtatSpecification.ARCHIVED &&
          v.etat !== EtatSpecification.EN_COURS,
      )
      .reduce(
        (max, v) => (v.version_nb > (max?.version_nb ?? 0) ? v : max),
        null,
      );
  });

  lastVersionState = computed<ChipState>(() => {
    switch (this.lastVersion()?.etat) {
      case EtatSpecification.EN_REDACTION:
      case EtatSpecification.VALIDEE:
      case EtatSpecification.REDIGEE:
      case EtatSpecification.APPROUVEE:
        return "secondary";
    }
  });

  articleInfos = computed(() => {
    if (this.articleId()) {
      return this.articleInfosFacade.getByArticleId(this.articleId())();
    } else if (this.articleInfosInput()) {
      return this.articleInfosInput();
    }
    return null;
  });

  constructor(private router: Router) {
    effect(() => {
      if (this.lastVersion() && !this.currentVersion()) {
        this.warning.emit(true);
      }
    });
    effect(() => {
      const id = this.articleId();
      if (id) {
        this.specificationFacade.loadByArticleId(id);
      }
    });
  }

  onNavigate() {
    this.router.navigateByUrl(
      `/specification/id/${this.articleInfos()?.articleId}`,
    );
  }

  onStats() {
    this.articleInfosFacade.onStats(this.articleInfos()?.articleId);
  }

  goToComptage() {
    this.router.navigate([`/articles/inventory`], {
      queryParams: { articleId: this.articleInfos()?.articleId },
    });
  }

  onStock() {
    this.router.navigate([`/stock/id/`], {
      queryParams: {
        articleId: this.articleInfos()?.articleId ?? this.articleId(),
      },
    });
  }
}
